import React, { useState, useEffect } from 'react';
import { Users, MessageSquare, X, Search, Trash2, Pencil } from 'lucide-react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import { teammateRequests, conversations as conversationsApi } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

export default function TeammateSearch({ eventType, eventId, eventName }) {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [myRequest, setMyRequest] = useState(null);
  const [step, setStep] = useState(0); // 0=closed, 1=count, 2=description, 3=confirm, 4=edit
  const [count, setCount] = useState(1);
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);

  const fetchRequests = async () => {
    try {
      const r = await teammateRequests.getForEvent(eventType, eventId);
      setRequests(r.data);
      if (user) {
        const mine = r.data.find(req => req.user_id === user.id);
        setMyRequest(mine || null);
      }
    } catch {}
  };

  useEffect(() => { fetchRequests(); }, [eventType, eventId, user]);

  const handleSubmit = async () => {
    setLoading(true);
    try {
      await teammateRequests.create({
        event_type: eventType, event_id: eventId,
        teammates_needed: count, description: description.trim(),
      });
      toast.success('Recherche de coéquipier publiée !');
      setStep(3);
      fetchRequests();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
    setLoading(false);
  };

  const handleUpdate = async () => {
    if (!myRequest) return;
    setLoading(true);
    try {
      await teammateRequests.update(myRequest.id, {
        teammates_needed: count, description: description.trim(),
      });
      toast.success('Recherche mise à jour');
      setStep(0);
      fetchRequests();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
    setLoading(false);
  };

  const handleRemove = async () => {
    if (!myRequest) return;
    setLoading(true);
    try {
      await teammateRequests.remove(myRequest.id);
      toast.success('Recherche retirée');
      setMyRequest(null);
      fetchRequests();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
    setLoading(false);
  };

  const handleContact = async (targetUserId) => {
    if (!isAuthenticated) {
      toast.error('Connectez-vous pour contacter un joueur');
      navigate('/login');
      return;
    }
    try {
      const r = await conversationsApi.list();
      const existing = r.data.find(c =>
        c.participants?.length === 2 &&
        c.participants.some(p => p.id === user.id) &&
        c.participants.some(p => p.id === targetUserId)
      );
      if (existing) {
        navigate(`/player/messages?conv=${existing.id}`);
      } else {
        const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL || ''}/api/conversations`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('access_token')}` },
          body: JSON.stringify({ participant_ids: [targetUserId] }),
        });
        const data = await resp.json();
        navigate(`/player/messages?conv=${data.id}`);
      }
    } catch {
      toast.error('Erreur lors de la création de la conversation');
    }
  };

  const openEdit = () => {
    if (myRequest) {
      setCount(myRequest.teammates_needed);
      setDescription(myRequest.description);
      setStep(4);
    }
  };

  return (
    <div className="mt-6" data-testid="teammate-search-section">
      {/* Action buttons */}
      <div className="flex items-center gap-2 mb-4">
        {isAuthenticated && !myRequest && (
          <Button onClick={() => setStep(1)} className="bg-[#4ECDC4] hover:bg-[#3dbdb5] text-black font-medium" data-testid="find-teammate-btn">
            <Users className="w-4 h-4 mr-2" /> Je cherche un/des coéquipier(s)
          </Button>
        )}
        {isAuthenticated && myRequest && (
          <>
            <Button onClick={openEdit} variant="outline" className="border-[#4ECDC4] text-[#4ECDC4] hover:bg-[#4ECDC4]/10" data-testid="edit-teammate-btn">
              <Pencil className="w-4 h-4 mr-2" /> Modifier ma recherche
            </Button>
            <Button onClick={handleRemove} variant="outline" className="border-red-500 text-red-400 hover:bg-red-500/10" data-testid="remove-teammate-btn" disabled={loading}>
              <Trash2 className="w-4 h-4 mr-2" /> Retirer ma candidature
            </Button>
          </>
        )}
      </div>

      {/* Requests list */}
      {requests.length > 0 && (
        <div className="space-y-2" data-testid="teammate-requests-list">
          <h3 className="text-sm font-semibold text-[#B0B0B0] flex items-center gap-2 mb-3">
            <Search className="w-4 h-4" /> Joueurs cherchant des coéquipiers ({requests.length})
          </h3>
          {requests.map(req => (
            <div key={req.id} className={`flex items-start gap-3 p-3 rounded-lg border transition-colors ${
              req.user_id === user?.id ? 'bg-[#4ECDC4]/5 border-[#4ECDC4]/30' : 'bg-[#1a1a1a] border-[#333]'
            }`} data-testid={`teammate-request-${req.id}`}>
              <div className="w-9 h-9 rounded-full bg-[#4ECDC4]/20 flex items-center justify-center shrink-0">
                <Users className="w-4 h-4 text-[#4ECDC4]" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-white text-sm">{req.pseudo}</span>
                  <span className="text-xs bg-[#4ECDC4]/20 text-[#4ECDC4] px-2 py-0.5 rounded-full">
                    Cherche {req.teammates_needed} coéquipier{req.teammates_needed > 1 ? 's' : ''}
                  </span>
                  {req.city && <span className="text-xs text-[#666]">{req.city}</span>}
                  {req.user_id === user?.id && <span className="text-xs text-[#4ECDC4]">(vous)</span>}
                </div>
                {req.description && <p className="text-xs text-[#999] mt-1 line-clamp-2">{req.description}</p>}
              </div>
              {req.user_id !== user?.id && (
                <Button size="sm" variant="ghost"
                  onClick={() => isAuthenticated ? handleContact(req.user_id) : navigate('/login')}
                  className="text-[#4ECDC4] hover:bg-[#4ECDC4]/10 shrink-0" data-testid={`contact-teammate-${req.id}`}>
                  <MessageSquare className="w-4 h-4" />
                </Button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Step 1: Choose count */}
      <Dialog open={step === 1} onOpenChange={(o) => !o && setStep(0)}>
        <DialogContent className="bg-[#141414] border-[#333] text-white max-w-sm">
          <DialogHeader><DialogTitle>Nombre de coéquipiers recherchés</DialogTitle></DialogHeader>
          <div className="flex gap-3 mt-4">
            {[1, 2].map(n => (
              <button key={n} onClick={() => { setCount(n); setStep(2); }}
                className="flex-1 p-4 rounded-lg border-2 border-[#333] hover:border-[#4ECDC4] bg-[#0a0a0a] hover:bg-[#4ECDC4]/5 transition-all text-center"
                data-testid={`teammate-count-${n}`}>
                <Users className="w-6 h-6 mx-auto mb-2 text-[#4ECDC4]" />
                <span className="text-lg font-bold text-white">{n}</span>
                <span className="block text-xs text-[#888] mt-1">coéquipier{n > 1 ? 's' : ''}</span>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Step 2: Description */}
      <Dialog open={step === 2} onOpenChange={(o) => !o && setStep(0)}>
        <DialogContent className="bg-[#141414] border-[#333] text-white max-w-md">
          <DialogHeader><DialogTitle>Dites-en plus</DialogTitle></DialogHeader>
          <Textarea
            value={description} onChange={e => setDescription(e.target.value)}
            placeholder="Renseignez votre recherche, présentez-vous, vos commandant(s) ou style de jeu, control, aggro, combo..."
            className="bg-[#0a0a0a] border-[#333] text-white min-h-[120px] mt-3"
            data-testid="teammate-description-input"
          />
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setStep(1)} className="border-[#333] text-white">Retour</Button>
            <Button onClick={handleSubmit} disabled={loading} className="bg-[#4ECDC4] hover:bg-[#3dbdb5] text-black" data-testid="teammate-submit-btn">
              {loading ? 'Publication...' : 'Publier ma recherche'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Step 3: Confirmation */}
      <Dialog open={step === 3} onOpenChange={(o) => !o && setStep(0)}>
        <DialogContent className="bg-[#141414] border-[#333] text-white max-w-sm text-center">
          <div className="py-4">
            <div className="w-14 h-14 rounded-full bg-[#4ECDC4]/20 flex items-center justify-center mx-auto mb-4">
              <Users className="w-7 h-7 text-[#4ECDC4]" />
            </div>
            <h3 className="text-lg font-bold mb-2">Recherche publiée !</h3>
            <p className="text-sm text-[#999]">Les autres joueurs peuvent maintenant vous contacter pour rejoindre votre équipe.</p>
            <Button onClick={() => setStep(0)} className="mt-4 bg-[#4ECDC4] hover:bg-[#3dbdb5] text-black" data-testid="teammate-confirm-close">Fermer</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Step 4: Edit */}
      <Dialog open={step === 4} onOpenChange={(o) => !o && setStep(0)}>
        <DialogContent className="bg-[#141414] border-[#333] text-white max-w-md">
          <DialogHeader><DialogTitle>Modifier ma recherche</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-3">
            <div>
              <label className="text-sm text-[#B0B0B0] mb-2 block">Nombre de coéquipiers</label>
              <div className="flex gap-2">
                {[1, 2].map(n => (
                  <button key={n} onClick={() => setCount(n)}
                    className={`flex-1 p-3 rounded-lg border-2 text-center transition-all ${
                      count === n ? 'border-[#4ECDC4] bg-[#4ECDC4]/10 text-[#4ECDC4]' : 'border-[#333] bg-[#0a0a0a] text-white'
                    }`} data-testid={`edit-count-${n}`}>
                    {n} coéquipier{n > 1 ? 's' : ''}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-sm text-[#B0B0B0] mb-2 block">Description</label>
              <Textarea value={description} onChange={e => setDescription(e.target.value)}
                className="bg-[#0a0a0a] border-[#333] text-white min-h-[100px]" data-testid="edit-teammate-description" />
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setStep(0)} className="border-[#333] text-white">Annuler</Button>
            <Button onClick={handleUpdate} disabled={loading} className="bg-[#4ECDC4] hover:bg-[#3dbdb5] text-black" data-testid="teammate-update-btn">
              {loading ? 'Mise à jour...' : 'Mettre à jour'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
